import { all, select, put, delay, call, race, take } from 'redux-saga/effects';
import i18n from 'i18next';
import { concurrentHandler, bufferedHandler, callback, evaluate } from '../../tools/effects';
import { generateLayer, createThumbnail, createThumbnailCSqueeze } from '../../../algorithms/imagery';
import { generateVisualizationParams } from '../../../algorithms/utils';
import { aggregateMissionsDates } from '../../../common/algorithms';
import { Types, Actions } from './header';
import { Actions as Imagery } from '../imagery';
import { getMepBayParameters, getImageryIdentifiers } from '../../../selectors';
import { ee } from '../../../services/earth-engine';
import {
  applyMndwiBand,
  applyNdviBand,
  applyScaleFactors,
  applyUiBand,
  filterElevation,
  maskCloudLandsat,
  maskCloudSentinel,
} from '../../../algorithms/csqueeze';

const toDateOnly = (date) => new Date(date).toISOString().slice(0, 10);

// period.start/period.end (and similar upstream dates) can carry a
// sub-day/local-timezone time-of-day component instead of landing exactly on
// UTC midnight. Rounding to UTC midnight up front means every comparison and
// the final toDateOnly() string always agree.
const toUtcMidnight = (date) => new Date(`${toDateOnly(date)}T00:00:00.000Z`);

// Splits the requested range into one-year chunks (oldest first), so each Earth Engine request
// only needs to walk a year of revisit cycles instead of the mission's entire history.
const buildYearlyChunks = (rangeStart, rangeEnd) => {
  const start = toUtcMidnight(rangeStart);
  const end = toUtcMidnight(rangeEnd);
  const chunks = [];
  for (let year = start.getUTCFullYear(); year <= end.getUTCFullYear(); year++) {
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const yearEnd = new Date(Date.UTC(year, 11, 31));
    const chunkStart = yearStart > start ? yearStart : start;
    const chunkEnd = yearEnd < end ? yearEnd : end;
    if (chunkStart >= chunkEnd) continue;
    chunks.push({ start: toDateOnly(chunkStart), end: toDateOnly(chunkEnd) });
  }
  return chunks;
};

// Two adjacent year-chunks each independently phase-align their own cycle windows, so a real
// image sitting near a chunk boundary can occasionally surface twice, a few seconds/minutes apart.
const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const dedupeCloseDates = (dict) => {
  const sortedKeys = Object.keys(dict).sort((a, b) => new Date(a) - new Date(b));
  const result = {};
  let lastTime = null;
  for (const key of sortedKeys) {
    const time = new Date(key).getTime();
    if (lastTime !== null && time - lastTime < DEDUPE_WINDOW_MS) continue;
    result[key] = dict[key];
    lastTime = time;
  }
  return result;
};

// Isolates a single chunk's size check so one bad chunk can't fail the whole yield all().
function* fetchChunkSize(mission, geometry, chunk) {
  try {
    return yield call(
      evaluate,
      ee.ImageCollection(mission.name).filterBounds(geometry).filterDate(chunk.start, chunk.end).size(),
    );
  } catch (e) {
    console.error('[Mepbay] size check failed', mission.name, chunk, e);
    return 0;
  }
}

function* fetchChunkDates(mission, geometry, chunk) {
  try {
    return yield call(evaluate, mission.algorithms.queryAvailable(geometry, chunk.start, chunk.end));
  } catch (e) {
    console.error('[Mepbay] queryAvailable failed', mission.name, chunk, e);
    return null;
  }
}

function* fetchAllDates(mission, geometry, startDate, endDate) {
  const rangeStart = startDate || `${mission.startYear}-01-01`;
  const rangeEnd = endDate || (mission.endYear ? `${mission.endYear}-12-31` : new Date().toISOString());
  const chunks = buildYearlyChunks(rangeStart, rangeEnd);

  // Check each chunk's raw image count first (cheap: no windowing) and skip
  // queryAvailable entirely for chunks with zero images.
  const sizes = yield all(chunks.map((chunk) => call(fetchChunkSize, mission, geometry, chunk)));
  const nonEmptyChunks = chunks.filter((_, i) => sizes[i] > 0);

  const chunkResults = yield all(nonEmptyChunks.map((chunk) => call(fetchChunkDates, mission, geometry, chunk)));
  const merged = Object.assign({}, ...chunkResults.filter(Boolean));
  return dedupeCloseDates(merged);
}

/**
 * @function handleLoadAvailableImages
 * @description Queries each of the selected satellite's missions for available image dates within
 * the AOI and the given period, falling back to a secondary mission/collection when a mission has
 * no available dates and defines a fallback, then dispatches the aggregated available dates. The
 * query is scoped to `startDate`/`endDate` (chunked into yearly Earth Engine requests) instead of
 * always scanning the mission's entire history — this is what lets PeriodImageChooser search only
 * the period the user actually selected, instead of every image ever captured.
 * @generator
 * @param {Object} params.payload - { startDate, endDate } (both optional; omit for full history).
 * @yields {Object} select(getMepBayParameters) - Reads the AOI geometry and satellite configuration.
 * @yields {Promise} evaluate(mission.algorithms.queryAvailable) - Queries a mission's available dates.
 * @yields {Object} Actions.setMissionFallback - Dispatched when a mission needs its fallback collection.
 * @yields {Object} Actions.setAvailableDates - Dispatches the aggregated available dates/missions.
 * @see {@link aggregateMissionsDates}
 * **Called by:
 * @see {PeriodImageChooser} - MEPBay module's period/image chooser step, via Types.LOAD_AVAILABLE_IMAGES.
 * @memberof module:Mepbay
 * @returns {void}
 */
const handleLoadAvailableImages = function* ({ payload }) {
  const { startDate, endDate } = payload || {};
  const { satellite, geometry } = yield select(getMepBayParameters);

  if (!satellite || !satellite.missions) {
    console.error('Satellite or missions not set');
    return;
  }

  const missions = satellite.missions;
  let availableByMission = [];

  for (let mission of missions) {
    try {
      const dates = yield call(fetchAllDates, mission, geometry, startDate, endDate);
      let regionData = { name: mission.name, shortname: mission.shortname, dates };

      if (Object.keys(regionData.dates).length === 0 && mission.fallbackMission) {
        yield put(Actions.setMissionFallback(mission));

        const fallback = mission.fallbackMission;
        const fallbackDates = yield call(fetchAllDates, fallback, geometry, startDate, endDate);

        regionData = { name: fallback.name, shortname: fallback.shortname, dates: fallbackDates };
      }
      availableByMission.push(regionData);
    } catch (e) {
      console.log('Error', mission, e);
    }
  }

  const availableDates = aggregateMissionsDates(availableByMission);

  if (availableDates !== undefined) {
    yield put(Actions.setAvailableDates(availableDates, availableByMission));
  }
};

/**
 * @function handleAcquireImage
 * @description Acquires MEPBay imagery for either a single requested image, or a batch of yearly
 * median composites.
 * 1. If the payload carries a single `missionName`/`date` (from ImageChooserCard), acquires that
 *    image directly and pushes it as a layer.
 * 2. Otherwise, for each requested year: builds a median composite from its images, adds
 *    UI/NDVI/MNDWI bands, applies Landsat scale factors if needed, masks by elevation and clouds.
 *    This yearly-batch path is not currently reachable — `Mepbay.acquireImage` is only ever
 *    dispatched with the single-image shape.
 * 3. Dispatches each year's layer to the imagery state, spaced by 1 second to avoid overloading
 *    Earth Engine.
 * @param {Object} params.payload - Action payload: either {missionName, date} for a single image,
 * or {years, elevation} for a batch of yearly composites.
 * @generator
 * @yields {Object} select(getMepBayParameters) - Reads the AOI geometry and satellite configuration.
 * @yields {Object} Imagery.pushImageUrl - Dispatches each acquired/composited layer.
 * @yields {Promise} delay(1000) - Throttles dispatches between years.
 * @see {@link generateLayer}
 * @see {@link applyUiBand}
 * @see {@link applyNdviBand}
 * @see {@link applyMndwiBand}
 * @see {@link applyScaleFactors}
 * @see {@link filterElevation}
 * @see {@link maskCloudSentinel}
 * @see {@link maskCloudLandsat}
 * **Called by:
 * @see {ImageChooserCardMepBay} - via Types.ACQUIRE_IMAGE, single image path.
 * @memberof module:Mepbay
 * @returns {void}
 */
const handleAcquireImage = function* ({ payload }) {
  const { geometry, satellite } = yield select(getMepBayParameters);

  if (payload.missionName && payload.date) {
    const { missionName, date } = payload;

    try {
      const mission = satellite.get(missionName);
      const image = mission.algorithms.acquire(date, geometry);

      const layer = generateLayer(image, mission, `${mission.shortname}/${date}`);

      yield put(Imagery.pushImageUrl(`${mission.shortname}/${date}`, date, mission.name, null, null, image));

      console.log('Single image acquired for MepBay:', { missionName, date });
      return;
    } catch (error) {
      console.error('Error acquiring single image:', error);
      return;
    }
  }

  if (!payload.years) {
    console.error('Invalid payload for handleAcquireImage:', payload);
    return;
  }

  const { years, elevation } = payload;
  const layers = years.map((data) => {
    const year = data[0];
    const infos = data[1];

    let mission, missionInfo, medianImage, medianImageVisualize, falseColorVisualize, missionName;

    let imagesEE = ee.List(
      infos.map((info) => {
        const date = info.date;
        let end = new Date(date);
        end.setDate(end.getDate() + 1);

        missionName = info.name;

        mission = satellite.get(missionName);

        let image = ee.ImageCollection(missionName).filterBounds(geometry).filter(ee.Filter.date(date, end)).first();

        missionInfo = missionName.split('/');

        return image;
      }),
    );

    medianImage = ee.ImageCollection.fromImages(imagesEE);
    medianImage = medianImage.filterBounds(geometry);

    medianImage = medianImage.map((img) => {
      return img
        .addBands(applyUiBand(img, mission.bands))
        .addBands(applyNdviBand(img, mission.bands))
        .addBands(applyMndwiBand(img, mission.bands));
    });

    let bands = [mission.bands.red, mission.bands.green, mission.bands.blue];
    let bandsFalseColor = [mission.bands.nir, mission.bands.green, mission.bands.blue];

    if (missionInfo[0] === 'LANDSAT') medianImage = medianImage.map(applyScaleFactors);
    medianImage = medianImage
      .map((img) => filterElevation(img, elevation, null, mission.shortname))
      .map((img) => {
        if (mission.shortname === 'S2') {
          return maskCloudSentinel(img, mission);
        }

        return maskCloudLandsat(img, mission.bands.qa, geometry);
      })
      .mosaic()
      .clip(geometry);

    var url = medianImageVisualize.getThumbURL({
      format: 'png',
      dimensions: 800,
      tileScale: 512,
    });

    return {
      url,
      year,
      mission,
      imageObject: medianImage,
      classification: null,
    };
  });

  for (let i = 0; i < layers.length; i++) {
    const obj = layers[i];

    yield put(Imagery.pushImageUrl(obj.year, obj.year, obj.mission.name, obj.url, obj.falseColorUrl, obj.imageObject));
    yield delay(1000);
  }
};

/**
 * @function handleLoadThumbnails
 * @description Generates and caches a thumbnail (and cloud coverage) for every currently
 * available image date, dispatching each as metadata.
 * @generator
 * @yields {Object} select(getMepBayParameters) - Reads the AOI geometry, satellite, and available dates.
 * @yields {Promise} callback(createThumbnail|createThumbnailCSqueeze) - Renders the image's thumbnail URL.
 * @yields {Promise} evaluate(image.get('CLOUDS')) - Evaluates the image's cloud coverage.
 * @yields {Object} Actions.insertMetadata - Dispatches the thumbnail/cloud metadata for the image.
 * **Called by:
 * @see {PeriodImageChooser} - MEPBay module's period/image chooser step, via Types.LOAD_THUMBNAILS.
 * @memberof module:Mepbay
 * @returns {void}
 */
function* fetchOneThumbnail(entry, id, satellite, geometry, thumbnailFn) {
  const mission = satellite.get(entry.name);
  const image = mission.algorithms.acquire(entry.date, geometry);
  const visParam = generateVisualizationParams(mission);
  const url = yield callback(thumbnailFn, image, geometry, visParam);
  const clouds = yield evaluate(image.get('CLOUDS'));
  window.sessionStorage.setItem(entry.id + '_thumbnail', JSON.stringify({ url, clouds }));
  yield put(Actions.insertMetadata(entry.name, entry.date, { id, thumbnail: url, clouds }));
}

const THUMBNAIL_WORKER_COUNT = 6;

function* thumbnailWorker(queue, satellite, geometry, thumbnailFn) {
  while (queue.length) {
    const { entry, id } = queue.shift();
    yield call(fetchOneThumbnail, entry, id, satellite, geometry, thumbnailFn);
  }
}

const handleLoadThumbnails = function* (action) {
  let { geometry, satellite, availableDates } = yield select(getMepBayParameters);
  if (action?.payload?.images) availableDates = action.payload.images;
  const metadata = yield select((state) => state.mepbay.metadata);
  const alreadyFetched = new Set(metadata.map((m) => `${m.date}-${m.missionName}`));
  const thumbnailFn = window.location.href.indexOf('csqueeze') === -1 ? createThumbnail : createThumbnailCSqueeze;

  const queue = availableDates
    .filter((entry) => !alreadyFetched.has(`${entry.date}-${entry.name}`))
    .map((entry, id) => ({ entry, id }));

  const workerCount = Math.min(THUMBNAIL_WORKER_COUNT, queue.length);
  yield all(Array.from({ length: workerCount }, () => call(thumbnailWorker, queue, satellite, geometry, thumbnailFn)));
};

/**
 * @function root
 * @description Root saga for the MEPBay duck: registers the handlers for loading available
 * images, acquiring images, and loading thumbnails.
 * @generator
 * @see {@link handleLoadAvailableImages}
 * @see {@link handleAcquireImage}
 * @see {@link handleLoadThumbnails}
 * **Called by:
 * @see {Redux-saga middleware} - registered as the "mepbay" saga.
 * @memberof module:Mepbay
 * @returns {void}
 */
const handleLoadThumbnailsCancellable = function* (action) {
  yield race({
    task: call(handleLoadThumbnails, action),
    cancel: take(Types.CANCEL_THUMBNAILS),
  });
};

const root = function* () {
  yield all([
    concurrentHandler(Types.LOAD_AVAILABLE_IMAGES, handleLoadAvailableImages),
    bufferedHandler(Types.ACQUIRE_IMAGE, handleAcquireImage),
    concurrentHandler(Types.LOAD_THUMBNAILS, handleLoadThumbnailsCancellable),
  ]);
};

export default root;
